import { APP_SCORING_MAX_SCORE, FEATURE_FLAGS } from '@/constants';
import { getScorePercentage } from './score-percentage-policy';
import { ISimulation, TScoreType, TSimulationType } from '@/types';

// ================================
// Strategy Types
// ================================

/**
 * Strategy for deciding which value should be used as the frontend score UUID.
 *
 * - score-id: prioritize score-level identity from nested payloads.
 * - session-id: prioritize session identity for store hydration consistency.
 */
export type TUuidStrategy = 'score-id' | 'session-id';

/**
 * Strategy for resolving completion timestamps.
 *
 * - api-timestamps: use top-level API lifecycle fields.
 * - score-payload: use nested score date when available.
 */
export type TDateCompletedStrategy = 'api-timestamps' | 'score-payload';

/**
 * Strategy for pass/fail resolution.
 *
 * - cert-pass: trust the backend-provided cert pass flag.
 * - derived: use computed pass/fail evaluation from score context.
 */
export type TPassStrategy = 'cert-pass' | 'derived';

/**
 * Strategy for simulation type resolution.
 *
 * - resolved-simulation: use type inferred from matched simulation metadata.
 * - score-payload: use simulation type carried by nested score payload.
 */
export type TSimulationTypeStrategy = 'resolved-simulation' | 'score-payload';

/**
 * Strategy for sparse value behavior.
 *
 * - raw: preserve sparse API values when possible.
 * - store-defaults: normalize sparse values to stable store defaults.
 */
export type TFallbackStrategy = 'raw' | 'store-defaults';

// ================================
// Category Mapping Policy
// ================================

/**
 * Maps one API category payload into the normalized frontend category shape.
 *
 * This policy handles schema drift between payload variants, including:
 * - skillPointMax vs scoreMax vs maxScore
 * - title vs name
 * - optional content item arrays
 * - inferred category type for pass/fail categories
 */
export const mapScoreCategoryPolicy = (apiCategory: any) => {
  // Prefer explicit max fields in order of most recent to legacy.
  const scoreMax = FEATURE_FLAGS.ENABLE_SCORECARD_V2
    ? (apiCategory.skillPointMax ??
      apiCategory.skill_point_max ??
      apiCategory.scoreMax ??
      apiCategory.maxScore ??
      APP_SCORING_MAX_SCORE)
    : APP_SCORING_MAX_SCORE;

  // Infer pass/fail category when score max is one and no explicit type exists.
  const categoryType = apiCategory.type || (scoreMax === 1 ? 'pass' : 'numeric');

  // Centralized percentage extraction supports both snake_case and camelCase payloads.
  const scorePercentage = getScorePercentage(apiCategory);

  return {
    score: apiCategory.score || 0,
    scorePercentage: scorePercentage !== null ? scorePercentage : undefined,
    scoreMax,
    pass: apiCategory.pass,
    skillLevel: apiCategory.skillLevel,
    title: apiCategory.title || apiCategory.name || '',
    type: categoryType,
    content: (apiCategory.content || []).map((contentItem: any) => ({
      title: contentItem.title,
      items: contentItem.items || [],
    })),
  };
};

// ================================
// Core Field Resolution Policies
// ================================

/**
 * Resolves completion date from top-level API timestamp fields.
 *
 * Priority order mirrors existing behavior:
 * 1. submitted_at
 * 2. updated_at
 * 3. created_at
 */
export const getCompletionDatePolicy = (apiScorecard: {
  submitted_at?: string | null;
  updated_at?: string | null;
  created_at: string;
}): string => {
  return apiScorecard.submitted_at || apiScorecard.updated_at || apiScorecard.created_at;
};

/**
 * Extracts score description with stable empty-string fallback.
 */
export const getScoreDescriptionPolicy = (scoreData: any): string => {
  if (!scoreData) return '';
  return scoreData?.description || '';
};

/**
 * Resolves session identifier with strategy-specific fallback sentinel values.
 *
 * - store-defaults: use -1 when session id is absent
 * - raw: use 0 when session id is absent
 */
export const resolveFallbackSessionIdPolicy = (
  sessionId: number | null | undefined,
  fallbackStrategy: TFallbackStrategy,
): number => {
  return sessionId || (fallbackStrategy === 'store-defaults' ? -1 : 0);
};

/**
 * Resolves mapped score UUID by strategy.
 */
export const resolveUuidPolicy = ({
  scoreId,
  scoreUuid,
  sessionId,
  uuidStrategy,
}: {
  scoreId?: number | string;
  scoreUuid?: string;
  sessionId: number;
  uuidStrategy: TUuidStrategy;
}): string => {
  if (uuidStrategy === 'session-id') {
    return sessionId.toString();
  }

  return scoreId?.toString() ?? scoreUuid?.toString() ?? '';
};

/**
 * Resolves simulation id for mapped scores.
 *
 * In raw mode, nested score simulation ids can backfill missing top-level fields.
 * In store-default mode, behavior stays strict to top-level scenario id.
 */
export const resolveSimulationIdPolicy = ({
  apiScenarioId,
  fallbackStrategy,
  scoreSimulationId,
}: {
  apiScenarioId?: number | null;
  fallbackStrategy: TFallbackStrategy;
  scoreSimulationId?: string;
}): string => {
  if (fallbackStrategy === 'raw') {
    return apiScenarioId?.toString() || scoreSimulationId || '';
  }

  return apiScenarioId?.toString() || '';
};

/**
 * Resolves final completion date by strategy.
 */
export const resolveDateCompletedPolicy = ({
  apiCompletionDate,
  dateCompletedStrategy,
  scoreDateCompleted,
}: {
  apiCompletionDate: string;
  dateCompletedStrategy: TDateCompletedStrategy;
  scoreDateCompleted?: string;
}): string => {
  if (dateCompletedStrategy === 'score-payload') {
    return scoreDateCompleted?.toString() ?? new Date().toISOString();
  }

  return apiCompletionDate;
};

/**
 * Resolves simulation type by combining strategy and fallback behavior.
 */
export const resolveSimulationTypePolicy = ({
  fallbackStrategy,
  scoreSimulationType,
  simulationType,
  simulationTypeStrategy,
}: {
  fallbackStrategy: TFallbackStrategy;
  scoreSimulationType?: TSimulationType;
  simulationType?: TSimulationType;
  simulationTypeStrategy: TSimulationTypeStrategy;
}): TSimulationType | undefined => {
  if (simulationTypeStrategy === 'score-payload') {
    return scoreSimulationType;
  }

  if (fallbackStrategy === 'store-defaults') {
    return simulationType;
  }

  return simulationType ?? scoreSimulationType;
};

/**
 * Resolves final pass value by strategy and fallback policy.
 *
 * Notable behavior:
 * - store-defaults converts null derived values to undefined
 * - raw mode preserves rawCertPass fallback when derived is null
 */
export const resolvePassPolicy = ({
  fallbackStrategy,
  passStrategy,
  rawCertPass,
  derivedPass,
}: {
  fallbackStrategy: TFallbackStrategy;
  passStrategy: TPassStrategy;
  rawCertPass?: boolean;
  derivedPass?: boolean | null;
}) => {
  if (passStrategy === 'cert-pass') {
    return rawCertPass;
  }

  if (fallbackStrategy === 'store-defaults') {
    return derivedPass !== null ? derivedPass : undefined;
  }

  return derivedPass ?? rawCertPass;
};

// ================================
// V2-Specific Policies
// ================================

/**
 * Infers simulation type from v2 boolean flags.
 */
export const getSimulationTypeFromV2FlagsPolicy = (apiScorecard: {
  is_practice?: boolean | null;
  is_certification?: boolean | null;
}): TSimulationType => {
  if (apiScorecard.is_practice === true) return 'practice';
  if (apiScorecard.is_certification === true) return 'certification';
  return 'role-play';
};

/**
 * Resolves v2 date completed value. This currently preserves identical behavior
 * across both supported strategies to maintain parity with current mapping flows.
 */
export const resolveV2DateCompletedPolicy = ({
  dateCompleted,
  dateCompletedStrategy,
}: {
  dateCompleted?: string;
  dateCompletedStrategy: TDateCompletedStrategy;
}): string => {
  if (dateCompletedStrategy === 'score-payload') {
    return dateCompleted || new Date().toISOString();
  }

  return dateCompleted || new Date().toISOString();
};

/**
 * Resolves v2 metadata block with fallback to related certification/simulation context.
 */
export const resolveV2MetadataPolicy = ({
  apiScorecard,
  certification,
  simulation,
}: {
  apiScorecard: {
    attempts_since_last_unlock?: number | null;
    certification_title?: string | null;
    certification_title_short?: string | null;
    score_percentage?: number | null;
    simulation_title?: string | null;
    total_attempts?: number | null;
  };
  certification?: {
    title?: string | null;
    titleShort?: string | null;
  };
  simulation?: ISimulation;
}) => {
  return {
    attemptsSinceLastUnlock: apiScorecard.attempts_since_last_unlock ?? undefined,
    certificationTitle: apiScorecard.certification_title || certification?.title || undefined,
    certificationTitleShort:
      apiScorecard.certification_title_short || certification?.titleShort || undefined,
    scorePercentage: apiScorecard.score_percentage ?? undefined,
    simulationTitle: apiScorecard.simulation_title || simulation?.title || undefined,
    totalAttempts: apiScorecard.total_attempts ?? undefined,
  };
};

/**
 * Resolves score type with optional store-default numeric fallback.
 */
export const resolveScoreTypePolicy = ({
  fallbackStrategy,
  scoreType,
}: {
  fallbackStrategy: TFallbackStrategy;
  scoreType?: TScoreType | null;
}): TScoreType | undefined => {
  return scoreType || (fallbackStrategy === 'store-defaults' ? 'numeric' : undefined);
};
